/**
 * Account · Profile (immersive account view, tab 1 of 5) — PATTERN LEADER.
 *
 * The full-immersion replacement for the cramped genie-hub/account-settings
 * Profile tab. Profile details (email, member since, avatar, full name,
 * language, timezone) as a hero card on the left; Team Management + Team
 * Members stacked on the right. Same content as the live Profile tab, given
 * room to breathe.
 *
 * Re-skins the live ProfileSection / TeamManagementSection / TeamMembersSection
 * into the shared AcctPageHead + AcctCard / AcctField / AcctButton rhythm the
 * other four account tabs copy. All data via the REAL useAuth(); all writes via
 * the existing UserService methods. No business logic is re-implemented.
 */
import { useEffect, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Loader2, Mail, Pencil, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { UserService } from "@/services/users/UserService";
import { LANGUAGE_FLAGS } from "@/utils/localization/language";
import { Roles, type TeamMember } from "@/types/auth/auth";
import {
  AcctButton,
  AcctCard,
  AcctField,
  AcctPageHead,
  ACCT_BRAND as B,
} from "@/components/voice-account/VoiceAccountDetail";
import { ProfileExtraDetailsSheet } from "./ProfileExtraDetailsSheet";

const TIMEZONES = [
  "UTC",
  "Atlantic/Azores",
  "Atlantic/Cape_Verde",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Europe/Istanbul",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Manila",
  "Pacific/Samoa",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Perth",
  "Australia/Adelaide",
  "Pacific/Auckland",
  "Pacific/Fiji",
];

const INPUT_CLASS =
  "h-[46px] w-full px-3.5 rounded-xl border border-genie-border bg-white text-[14.5px] text-[#222B45] focus:outline-none focus:border-genie-primary focus:ring-2 focus:ring-genie-primary/15 transition-all";

const SECTION_LABEL =
  "text-xs font-bold tracking-[0.6px] uppercase text-gray-500";

const FIELD_LABEL = "text-[12.5px] font-semibold text-[#64748B]";

function initialsFrom(name?: string | null, email?: string | null): string {
  const base = (name || email || "").trim();
  if (!base) return "HG";
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

function memberTint(index: number): string {
  const cycle = [B.blue, B.purple, B.amber];
  return cycle[index % cycle.length];
}

const ProfileTab = () => {
  const { user, userProfile, team, refreshTeam } = useAuth();

  /* ── profile (re-skin of ProfileSection) ── */
  const [fullName, setFullName] = useState("");
  const [avatar, setAvatar] = useState<string | undefined>("");
  const [timezone, setTimezone] = useState("");
  const [useCustomTimezone, setUseCustomTimezone] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false);

  /* ── team management (re-skin of TeamManagementSection) ── */
  const [teamName, setTeamName] = useState("");
  const [teamBrand, setTeamBrand] = useState("");
  const [editingTeam, setEditingTeam] = useState(false);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);

  /* ── team members (re-skin of TeamMembersSection) ── */
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const isOwner = !!team && team.owner_id === user?.id;
  const canCreateTeam =
    userProfile?.role === Roles.Admin || userProfile?.role === Roles.Business;

  useEffect(() => {
    if (user && userProfile) {
      setFullName(userProfile.full_name || "");
      setAvatar(userProfile.avatar_url || undefined);
      const tz = userProfile.timezone || "";
      setTimezone(tz);
      setUseCustomTimezone(!!tz && !TIMEZONES.includes(tz));
      setPreferredLanguage(userProfile.preferred_language || "");
    }
  }, [user, userProfile]);

  useEffect(() => {
    if (team) {
      setTeamName(team.name || "");
      setTeamBrand(team.brand || "");
    }
  }, [team]);

  useEffect(() => {
    if (team) loadTeamMembers();
    else setTeamMembers([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team]);

  const loadTeamMembers = async () => {
    if (!team) return;
    setMembersLoading(true);
    try {
      const teamData = await UserService.getTeamData(team.id, team.owner_id);
      if (!teamData) {
        setTeamMembers([]);
        return;
      }
      const { data: invitations } = await supabase
        .from("team_invitations")
        .select("*")
        .eq("team_id", team.id)
        .eq("status", "pending");

      const allMembers: TeamMember[] = [
        ...teamData.members,
        ...(invitations || []).map((invite) => ({
          id: invite.id,
          full_name: null,
          email: invite.email,
          avatar_url: null,
          role: "Invited" as const,
        })),
      ];
      setTeamMembers(allMembers);
    } catch (err) {
      console.error("Error loading team members:", err);
      toast.error("We couldn't load your team members. Please try again.");
      setTeamMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSavingProfile(true);
    try {
      await UserService.updateUserProfile(user.id, {
        full_name: fullName,
        avatar_url: avatar,
        timezone: timezone || null,
        preferred_language: preferredLanguage || null,
      });
      toast.success("Profile updated successfully.");
    } catch (err: any) {
      toast.error(`We couldn't save your profile: ${err.message}`);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast.error("Team name is required.");
      return;
    }
    if (!user?.id) {
      toast.error("We couldn't find your account.");
      return;
    }
    setSavingTeam(true);
    const loadingToast = toast.loading("Creating your team...");
    try {
      await UserService.createTeam(teamName, teamBrand, user.id);
      setCreatingTeam(false);
      await refreshTeam();
      toast.success("Team created. You're now the team owner.", { id: loadingToast });
    } catch (err: any) {
      toast.error(`We couldn't create your team: ${err.message}`, { id: loadingToast });
    } finally {
      setSavingTeam(false);
    }
  };

  const handleUpdateTeam = async () => {
    if (!team || !user) return;
    setSavingTeam(true);
    const loadingToast = toast.loading("Updating team details...");
    try {
      await UserService.updateTeam(team.id, teamName, teamBrand);
      setEditingTeam(false);
      await refreshTeam();
      toast.success("Team updated successfully.", { id: loadingToast });
    } catch (err: any) {
      toast.error(`We couldn't update your team: ${err.message}`, { id: loadingToast });
    } finally {
      setSavingTeam(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !/^\S+@\S+\.\S+$/.test(inviteEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!team) {
      toast.error("Please create a team first before inviting members.");
      return;
    }
    setInviting(true);
    const loadingToast = toast.loading(`Sending invitation to ${inviteEmail}...`);
    try {
      await UserService.inviteMember(inviteEmail, team, userProfile, user?.id || "");
      await loadTeamMembers();
      toast.success(`Invitation sent. ${inviteEmail} will receive an email to join your team.`, {
        id: loadingToast,
        duration: 5000,
      });
      setInviteEmail("");
    } catch (err: any) {
      toast.error(err.message || "We couldn't send that invitation.", { id: loadingToast });
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (
    memberId: string,
    email: string | null,
    role: string
  ) => {
    if (!team || !user) return;
    const loadingToast = toast.loading(
      role === "Invited"
        ? `Cancelling invitation for ${email}...`
        : `Removing ${email} from team...`
    );
    try {
      if (role === "Invited") {
        await UserService.cancelInvitation(memberId, team.id);
        toast.success(`Invitation cancelled for ${email}.`, { id: loadingToast });
      } else {
        await UserService.removeTeamMember(team.id, memberId, user.id);
        toast.success(`${email} has been removed from the team.`, { id: loadingToast });
      }
      await loadTeamMembers();
    } catch (err: any) {
      toast.error(err.message || "We couldn't remove that member.", { id: loadingToast });
    }
  };

  const cancelEditTeam = () => {
    setEditingTeam(false);
    setTeamName(team?.name || "");
    setTeamBrand(team?.brand || "");
  };

  /* ── loading skeleton state ── */
  if (userProfile === undefined) {
    return (
      <>
        <AcctPageHead
          icon="profile"
          title="Profile"
          subtitle="Your details and who else can manage your genies."
          tint={B.blue}
        />
        <div className="grid grid-cols-1 lg:grid-cols-[1.45fr_1fr] gap-5 items-start">
          <AcctCard label="Profile">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-[72px] h-[72px] rounded-full bg-gray-100 animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-5 w-40 rounded bg-gray-100 animate-pulse" />
                <div className="h-3.5 w-28 rounded bg-gray-100 animate-pulse" />
              </div>
            </div>
            <div className="space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-1.5">
                  <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
                  <div className="h-[46px] rounded-xl bg-gray-100 animate-pulse" />
                </div>
              ))}
            </div>
          </AcctCard>
          <div className="flex flex-col gap-5">
            {[0, 1].map((i) => (
              <AcctCard key={i}>
                <div className="space-y-4">
                  <div className="h-3 w-32 rounded bg-gray-100 animate-pulse" />
                  <div className="h-[46px] rounded-xl bg-gray-100 animate-pulse" />
                  <div className="h-[46px] rounded-xl bg-gray-100 animate-pulse" />
                </div>
              </AcctCard>
            ))}
          </div>
        </div>
      </>
    );
  }

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

  const initials = initialsFrom(userProfile?.full_name, userProfile?.email);

  const languages = Object.entries(LANGUAGE_FLAGS)
    .filter(([code]) => code !== "default")
    .sort(([, a], [, b]) => a.name.localeCompare(b.name));

  return (
    <>
      <AcctPageHead
        icon="profile"
        title="Profile"
        subtitle="Your details and who else can manage your genies."
        tint={B.blue}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1.45fr_1fr] gap-5 items-start">
        {/* ── Left — hero profile card ── */}
        <AcctCard label="Profile">
          {/* avatar + name banner */}
          <div className="flex items-center gap-4 mb-[22px]">
            {avatar ? (
              <img
                src={avatar}
                alt={fullName || "Profile"}
                className="w-[72px] h-[72px] rounded-full object-cover flex-shrink-0 shadow-[0_10px_26px_-12px_rgba(78,156,255,0.6)]"
              />
            ) : (
              <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] flex items-center justify-center flex-shrink-0 shadow-[0_10px_26px_-12px_rgba(78,156,255,0.6)]">
                <span className="text-[26px] font-extrabold text-white tracking-wide">
                  {initials}
                </span>
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[19px] font-extrabold text-[#222B45] tracking-[-0.3px] truncate">
                <CopyableEmail email={fullName || userProfile?.email} fallback="Your account" textClassName="truncate" />
              </p>
              <p className="text-[13px] text-[#64748B] mt-0.5">
                Member since {memberSince}
              </p>
            </div>
          </div>

          {/* fields */}
          <div className="flex flex-col gap-4">
            <AcctField
              label="Email address"
              value={user?.email || "—"}
              hint="Used to sign in. Contact support to change it."
            />

            <div className="flex flex-col gap-1.5">
              <label htmlFor="hg-fullname" className={FIELD_LABEL}>
                Full name
              </label>
              <input
                id="hg-fullname"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
                className={INPUT_CLASS}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="hg-language" className={FIELD_LABEL}>
                  Preferred language
                </label>
                <select
                  id="hg-language"
                  value={preferredLanguage}
                  onChange={(e) => setPreferredLanguage(e.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value="">Select a language...</option>
                  {languages.map(([code, { flag, name }]) => (
                    <option key={code} value={code}>
                      {flag} {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="hg-timezone" className={FIELD_LABEL}>
                    Timezone
                  </label>
                  <button
                    type="button"
                    onClick={() => setUseCustomTimezone((v) => !v)}
                    className="text-[11px] font-bold text-genie-primary hover:opacity-80 transition-opacity"
                  >
                    {useCustomTimezone ? "Use list" : "Custom"}
                  </button>
                </div>
                {useCustomTimezone ? (
                  <input
                    id="hg-timezone"
                    type="text"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    placeholder="e.g. UTC+5:30"
                    className={INPUT_CLASS}
                  />
                ) : (
                  <select
                    id="hg-timezone"
                    value={timezone || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTimezone(value);
                      if (value && !TIMEZONES.includes(value)) setUseCustomTimezone(true);
                    }}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select timezone...</option>
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* actions */}
          <div className="flex flex-wrap gap-2.5 mt-[22px]">
            <button
              type="button"
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="h-10 px-4 rounded-full border border-transparent inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {savingProfile && <Loader2 className="w-4 h-4 animate-spin" />}
              {savingProfile ? "Saving..." : "Save profile"}
            </button>
            <AcctButton
              label="Edit extra details"
              kind="ghost"
              icon="profile"
              onClick={() => setExtraOpen(true)}
            />
          </div>
        </AcctCard>

        {/* ── Right — team management + members ── */}
        <div className="flex flex-col gap-5">
          {/* team management */}
          <AcctCard>
            <div className="flex items-center justify-between mb-4">
              <p className={SECTION_LABEL}>
                {isOwner ? "Team management" : "My team"}
              </p>
              {team && !editingTeam && isOwner && (
                <button
                  type="button"
                  onClick={() => setEditingTeam(true)}
                  className="h-7 px-3 rounded-full border border-genie-border inline-flex items-center gap-1.5 text-xs font-bold text-genie-primary hover:bg-genie-primary/5 transition-colors"
                >
                  <Pencil className="w-3 h-3" />
                  Edit
                </button>
              )}
            </div>

            {!team ? (
              !creatingTeam ? (
                <div className="text-center py-2">
                  <p className="text-[13.5px] text-[#64748B] mb-4">
                    You haven't created a team yet.
                  </p>
                  {canCreateTeam ? (
                    <AcctButton
                      label="Create team"
                      kind="solid"
                      onClick={() => setCreatingTeam(true)}
                    />
                  ) : (
                    <div className="px-3 py-2.5 rounded-xl bg-genie-accent/10 border border-genie-accent/30 text-[12.5px] text-genie-accent-dark">
                      Team creation is only available for business accounts.
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className={FIELD_LABEL}>Team name</label>
                    <input
                      type="text"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      placeholder="Your team name"
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className={FIELD_LABEL}>Brand name</label>
                    <input
                      type="text"
                      value={teamBrand}
                      onChange={(e) => setTeamBrand(e.target.value)}
                      placeholder="Your brand name"
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div className="flex gap-2.5">
                    <button
                      type="button"
                      onClick={handleCreateTeam}
                      disabled={savingTeam}
                      className="h-10 px-4 rounded-full border border-transparent inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90 disabled:opacity-60"
                    >
                      {savingTeam && <Loader2 className="w-4 h-4 animate-spin" />}
                      {savingTeam ? "Creating..." : "Create team"}
                    </button>
                    <AcctButton
                      label="Cancel"
                      kind="ghost"
                      onClick={() => setCreatingTeam(false)}
                    />
                  </div>
                </div>
              )
            ) : editingTeam && isOwner ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className={FIELD_LABEL}>Team name</label>
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={FIELD_LABEL}>Brand name</label>
                  <input
                    type="text"
                    value={teamBrand}
                    onChange={(e) => setTeamBrand(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>
                <div className="flex gap-2.5">
                  <button
                    type="button"
                    onClick={handleUpdateTeam}
                    disabled={savingTeam}
                    className="h-10 px-4 rounded-full border border-transparent inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90 disabled:opacity-60"
                  >
                    {savingTeam && <Loader2 className="w-4 h-4 animate-spin" />}
                    {savingTeam ? "Saving..." : "Save changes"}
                  </button>
                  <AcctButton label="Cancel" kind="ghost" onClick={cancelEditTeam} />
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <AcctField label="Team name" value={team?.name || "—"} />
                <AcctField label="Brand name" value={team?.brand || "—"} />
                {!isOwner && (
                  <div className="px-3 py-2.5 rounded-xl bg-genie-accent/10 border border-genie-accent/30 text-[12.5px] text-genie-accent-dark">
                    You are a member of this team. Only the team owner can edit team settings.
                  </div>
                )}
              </div>
            )}
          </AcctCard>

          {/* team members */}
          <AcctCard label="Team members">
            <p className="text-[13px] text-[#64748B] -mt-1.5 mb-4 leading-relaxed">
              Team members can view and edit genies created under this account.
            </p>

            {membersLoading ? (
              <div className="flex flex-col gap-2.5">
                {[0, 1].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 py-3 border-t border-genie-border/60 first:border-t-0"
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-100 animate-pulse" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 w-28 rounded bg-gray-100 animate-pulse" />
                      <div className="h-3 w-40 rounded bg-gray-100 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !team ? (
              <div className="text-center py-6 rounded-xl border border-dashed border-genie-border bg-genie-background">
                <div className="w-11 h-11 mx-auto rounded-[13px] bg-genie-primary/10 flex items-center justify-center mb-3">
                  <UserPlus className="w-5 h-5 text-genie-primary" />
                </div>
                <p className="text-[13.5px] font-bold text-[#222B45]">No team yet</p>
                <p className="text-[12.5px] text-[#64748B] mt-1 px-6">
                  Create a team above to invite people to manage your genies with you.
                </p>
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="text-center py-6 rounded-xl border border-dashed border-genie-border bg-genie-background">
                <div className="w-11 h-11 mx-auto rounded-[13px] bg-genie-primary/10 flex items-center justify-center mb-3">
                  <UserPlus className="w-5 h-5 text-genie-primary" />
                </div>
                <p className="text-[13.5px] font-bold text-[#222B45]">No team members yet</p>
                <p className="text-[12.5px] text-[#64748B] mt-1 px-6">
                  Invite someone below to get started.
                </p>
              </div>
            ) : (
              <div className="flex flex-col">
                {teamMembers.map((member, i) => {
                  const tint =
                    member.role === "Owner" ? B.blue : memberTint(i);
                  const mInitials = initialsFrom(member.full_name, member.email);
                  return (
                    <div
                      key={member.id}
                      className={`flex items-center gap-3 py-3 ${
                        i === 0 ? "" : "border-t border-genie-border/60"
                      }`}
                    >
                      {member.avatar_url ? (
                        <img
                          src={member.avatar_url}
                          alt={member.full_name || member.email || "Member"}
                          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${tint}1f` }}
                        >
                          <span
                            className="text-[12.5px] font-extrabold"
                            style={{ color: tint }}
                          >
                            {mInitials}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[13.5px] font-bold text-[#222B45] truncate">
                          <CopyableEmail email={member.full_name || member.email} fallback="Unknown" textClassName="truncate" />
                        </p>
                        {member.full_name && member.email && (
                          <p className="text-[12px] text-gray-500 truncate">
                            <CopyableEmail email={member.email} textClassName="truncate" />
                          </p>
                        )}
                      </div>
                      <span
                        className="h-6 px-2.5 rounded-full inline-flex items-center text-[11px] font-bold flex-shrink-0"
                        style={
                          member.role === "Owner"
                            ? { backgroundColor: `${B.blue}14`, color: B.blue }
                            : { backgroundColor: "#F1F5F9", color: "#64748B" }
                        }
                      >
                        {member.role}
                      </span>
                      {member.role !== "Owner" && isOwner && (
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveMember(member.id, member.email, member.role)
                          }
                          title={
                            member.role === "Invited"
                              ? "Cancel invitation"
                              : "Remove member"
                          }
                          className="w-7 h-7 rounded-full inline-flex items-center justify-center text-gray-400 hover:text-genie-secondary hover:bg-genie-secondary/10 transition-colors flex-shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* invite row — owners only */}
            {team && isOwner && (
              <div className="mt-4 pt-4 border-t border-genie-border/60">
                <label
                  htmlFor="hg-invite"
                  className="block text-[12.5px] font-semibold text-[#64748B] mb-2"
                >
                  Invite a new member
                </label>
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <div className="flex-1 h-10 px-3.5 rounded-xl border border-genie-border bg-white flex items-center gap-2 focus-within:border-genie-primary focus-within:ring-2 focus-within:ring-genie-primary/15 transition-all">
                    <Mail className="w-[15px] h-[15px] text-gray-400 flex-shrink-0" />
                    <input
                      id="hg-invite"
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleInvite();
                      }}
                      placeholder="Enter email address"
                      className="flex-1 min-w-0 bg-transparent text-[13.5px] text-[#222B45] placeholder:text-gray-400 focus:outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleInvite}
                    disabled={inviting}
                    className="h-10 px-4 rounded-full border border-transparent inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90 disabled:opacity-60 flex-shrink-0"
                  >
                    {inviting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {inviting ? "Sending..." : "Send invite"}
                  </button>
                </div>
              </div>
            )}
          </AcctCard>
        </div>
      </div>

      <ProfileExtraDetailsSheet open={extraOpen} onOpenChange={setExtraOpen} />
    </>
  );
};

export default ProfileTab;
